import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';

import TextInput from 'common/text-input/text-input';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import { phoneNumberAddHypen } from 'constants/utils';
import useApi from 'hooks/useApi';
import { AxiosResponse } from 'axios';
import CryptoJS from 'crypto-js';
import useModalStore from 'zustands/useAlertConfirmStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useUserStore from 'zustands/useUserStore';
import useStoreStore from 'zustands/useStoreStore';
import moment from 'moment';
import useMinPayStore from 'zustands/useMinPayStore';
import { NoticePopupSlider, useNoticePopups } from 'components/notice-popup-slider';
import { LOGIN_POPUPS } from './login-popups';

const Background = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: space-around;
`;
const LoginForm = styled.div`
  display: flex;
  width: 20%;
  min-width: 300px;
  flex-direction: column;
`;
const Section = styled.section`
  margin-bottom: 20px;
`;
const ImgArea = styled.div``;
const SubmitBtn = styled.button`
  margin-bottom: 20px;
  height: 5vh;
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 4px;
  background-color: #bc1a21;
`;
const JoinBtn = styled.button``;
const BackgroundImg = styled.img``;

const TermsArea = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-top: 16px;
  white-space: nowrap;
  width: max-content;
  max-width: 90vw;
  align-self: flex-start;
`;
const TermsLink = styled.a`
  font-size: 11px;
  color: #999;
  text-decoration: none;
  white-space: nowrap;
  flex: 0 0 auto;

  &:hover {
    text-decoration: underline;
  }
`;
const TermsDivider = styled.span`
  font-size: 11px;
  color: #ccc;
  flex: 0 0 auto;
`;

// 번역 텍스트 정의
const translations = {
  ko: {
    phoneNumber: '휴대폰번호(ID)',
    phoneNumberPlaceholder: '휴대폰번호 입력',
    password: '비밀번호',
    passwordPlaceholder: '비밀번호 입력',
    login: '로그인',
    joinInfo: '회원가입은 앱에서 가능합니다.',
    phoneNumberError: '올바른 휴대폰번호 11자리를 입력해 주세요',
    passwordError: '비밀번호를 입력해 주세요.',
    loginTitle: '로그인',
  },
  en: {
    phoneNumber: 'Phone Number (ID)',
    phoneNumberPlaceholder: 'Enter phone number',
    password: 'Password',
    passwordPlaceholder: 'Enter password',
    login: 'Login',
    joinInfo: 'Sign up is available in the app.',
    phoneNumberError: 'Please enter a valid 11-digit phone number',
    passwordError: 'Please enter your password.',
    loginTitle: 'Login',
  },
  zn: {
    phoneNumber: '手机号码(ID)',
    phoneNumberPlaceholder: '输入手机号码',
    password: '密码',
    passwordPlaceholder: '输入密码',
    login: '登录',
    joinInfo: '注册可在应用程序中进行。',
    phoneNumberError: '请输入正确的11位手机号码',
    passwordError: '请输入密码。',
    loginTitle: '登录',
  },
};

const termsLinks = [
  { label: '서비스 이용약관', url: 'https://terms.wesop.co.kr/shopsol/terms-of-service-policy' },
  { label: '개인정보처리방침', url: 'https://terms.wesop.co.kr/shopsol/privacy-policy' },
  { label: '개인정보 수집 및 이용 동의', url: 'https://terms.wesop.co.kr/shopsol/personal-info-consent-policy' },
  { label: '위치기반서비스 이용약관', url: 'https://terms.wesop.co.kr/shopsol/location-based-service-terms-policy' },
  { label: '개인위치정보 이용약관', url: 'https://terms.wesop.co.kr/shopsol/personal-location-info-policy' },
  { label: '마케팅 활용 동의', url: 'https://terms.wesop.co.kr/shopsol/marketing-policy' },
];

const login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { webApiNode } = useApi();
  const { setModalState } = useModalStore();
  const { setUserInfo, setManagerInfo, setISP } = useUserStore();
  const { setStoreInfo } = useStoreStore();
  const { setMinPays } = useMinPayStore();

  // 쿼리스트링에서 language 파라미터 가져오기 (기본값: ko)
  const language = (searchParams.get('language') || 'ko') as 'ko' | 'en' | 'zn';
  const t = translations[language] || translations.ko;

  const [id, setId] = useState<string>('');
  const [pwd, setPwd] = useState<string>('');
  const [inputError, setInputError] = useState({
    name: '',
    message: '',
  });
  const [isPwdVisible, setIsPwdVisible] = useState<boolean>(false);
  const {
    visiblePopups,
    activeIndex: activePopupIndex,
    setActiveIndex: setActivePopupIndex,
    closeAll: closePopups,
    hideCurrentForever: hidePopupForever,
  } = useNoticePopups(LOGIN_POPUPS);
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name === 'loginID') {
      setId(phoneNumberAddHypen(value));
      if (value.length === 13) {
        pwdInputRef?.current?.focus();
      }
    }
    if (name === 'loginPWD') setPwd(value);
  };

  const handleEnterKeyOnPwdInput = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      loginFn();
    }
  };

  const loginFn = async () => {
    try {
      const ID = id.split('-').join('');
      if (ID.length !== 11) {
        idInputRef?.current?.focus();
        setInputError({ name: 'loginID', message: t.phoneNumberError });
        return;
      }
      if (pwd.length === 0) {
        pwdInputRef?.current?.focus();
        setInputError({ name: 'loginPWD', message: t.passwordError });
        return;
      }
      const ENCODING_KEY: string = process.env.REACT_APP_ENCODING_KEY ?? '';

      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/webmanagerLogin2`, {
        MobileNo: CryptoJS.AES.encrypt(ID, ENCODING_KEY).toString(),
        PASSWORD: CryptoJS.AES.encrypt(pwd, ENCODING_KEY).toString(),
      });
      if (data.resultCode === 0) {
        const { data: minSalaryData }: AxiosResponse['data'] = await webApiNode('get', `/auth/getMinSalaryInfo`);

        setMinPays(minSalaryData);

        setUserInfo({
          userId: '',
          userSeq: Number(data.MEMBER_SEQ),
          userToken: data.resultData,
          userType: data.resultType,
        });
        setISP(pwd === 'wesop12345');
        // language 쿼리스트링 유지
        const languageParam = language !== 'ko' ? `?language=${language}` : '';
        navigate(`/business-establishment-choice${languageParam}`);
      } else {
        return setModalState({
          isModalShow: true,
          title: t.loginTitle,
          desc: data.resultMsg,
        });
      }
    } catch (err) {
    } finally {
    }
  };

  const openJoinPage = () => {
    window.open('https://www.shop-sol.com/join', '_self');
  };

  const idInputRef = useRef<HTMLInputElement>(null);
  const pwdInputRef = useRef<HTMLInputElement>(null);

  // 이미지 클릭 시 새창으로 열기
  const handleImageClick = () => {
    window.open('https://www.notion.so/wesopkr/2026-02-02-2e94fd14810f80c193c7df214d94358d', '_blank');
  };

  React.useEffect(() => {
    // 한국시간 기준으로 현재 날짜 확인
    const nowKorea = moment().utcOffset(9); // UTC+9 (한국시간)
    const startDate = moment('2025-11-26').utcOffset(9); // 2025년 10월 1일 한국시간
    const endDate = moment('2025-11-27').utcOffset(9).endOf('day'); // 2025년 10월 12일 23:59:59 한국시간

    // 현재 시간이 10월 1일 ~ 10월 12일 사이인지 확인
    if (nowKorea.isBetween(startDate, endDate, null, '[]')) {
      setModalState({
        isModalShow: true,
        image: {
          url: 'https://cncgho4q9uj7.objectstorage.ap-seoul-1.oci.customer-oci.com/n/cncgho4q9uj7/b/ss-image/o/popup%2Foffice-blackout.jpg',
          width: 300,
          height: 420,
        },
      });
    }
    setStoreInfo({ storeSeq: '', storeName: '', storeSeqNumber: 0, isBN: false });
    // logoutFn();

    setUserInfo({ userId: '', userToken: '' });
    setISP(false);

    setManagerInfo({
      empSeq: 0,
      payShow: false,
      storePayShow: false,
      otherPayShow: false,
      calendarEdit: false,
      recievePush: false,
    });
  }, []);

  return (
    <WithSideBarAndNavBar>
      <Background>
        <LoginForm>
          <Section>
            <TextInput
              ref={idInputRef}
              value={id}
              inputData={handleInput}
              label={t.phoneNumber}
              placeholder={t.phoneNumberPlaceholder}
              inputName="loginID"
              errorMessage={inputError.name === 'loginID' ? inputError.message : ''}
              maxLength={13}
            />
          </Section>
          <Section style={{ position: 'relative' }}>
            <TextInput
              ref={pwdInputRef}
              value={pwd}
              onkeydown={handleEnterKeyOnPwdInput}
              inputData={handleInput}
              label={t.password}
              placeholder={t.passwordPlaceholder}
              inputName="loginPWD"
              errorMessage={inputError.name === 'loginPWD' ? inputError.message : ''}
              inputType={isPwdVisible ? 'text' : 'password'}
              /*  바말번호 표시 버튼 추가    */
            />
            <div
              style={{
                position: 'absolute',
                right: 12,
                top: 40,
                display: 'flex',
                alignItems: 'center',
              }}>
              <img
                src={isPwdVisible ? require('assets/icons/eye-on.png') : require('assets/icons/eye-off.png')}
                alt=""
                style={{ cursor: 'pointer' }}
                onClick={() => setIsPwdVisible(!isPwdVisible)}
              />
            </div>
          </Section>
          <SubmitBtn onClick={loginFn}>{t.login}</SubmitBtn>
          {/* <JoinBtn onClick={openJoinPage}>회원가입</JoinBtn> */}
          <p style={{ fontSize: '13px', color: '#999', textAlign: 'center' }}>{t.joinInfo}</p>
          <TermsArea>
            {termsLinks.map((link, index) => (
              <React.Fragment key={link.url}>
                {index > 0 && <TermsDivider>|</TermsDivider>}
                <TermsLink href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </TermsLink>
              </React.Fragment>
            ))}
          </TermsArea>
        </LoginForm>
        <ImgArea>
          <BackgroundImg src={require('assets/images/myshop.png')} />
        </ImgArea>
      </Background>

      {/* 공지 팝업 (기간 겹치면 슬라이드) */}
      <NoticePopupSlider
        popups={visiblePopups}
        activeIndex={activePopupIndex}
        onChangeIndex={setActivePopupIndex}
        onClose={closePopups}
        onHideCurrentForever={hidePopupForever}
      />
    </WithSideBarAndNavBar>
  );
};

export default login;
